import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/common/Modal';
import MarketingValidationModal from '../../components/supply/MarketingValidationModal';
import MarketingReviewModal from '../../components/validation/MarketingReviewModal';
import ForceDeVenteValidationModal from '../../components/validation/ForceDeVenteValidationModal';
import DischargeUploadModal from '../../components/validation/DischargeUploadModal';
import DirectriceValidationModal from '../../components/validation/DirectriceValidationModal';
import CommercialValidationModal from '../../components/validation/CommercialValidationModal';
import DistributeurValidationModal from '../../components/validation/DistributeurValidationModal';
import SupplySourceHistory from '../../components/supply/SupplySourceHistory';
import WorkflowStepper from '../../components/WorkflowStepper';
import { getStatusBadge } from '../../utils/requestStatus';

const RequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  // L'intérimaire garde son rôle ; seule l'étape directrice lui est ouverte en plus.
  const actsAsDirectrice = user?.role === 'directrice_marketing' || user?.actingDirectrice === true;
  const [request, setRequest] = useState(null);
  const [products, setProducts] = useState([]);
  const [validations, setValidations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validationLoading, setValidationLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showMarketingValidationModal, setShowMarketingValidationModal] = useState(false);
  const [showMarketingReviewModal, setShowMarketingReviewModal] = useState(false);
  const [showApprobationModal, setShowApprobationModal] = useState(false);
  const [showForceDeVenteModal, setShowForceDeVenteModal] = useState(false);
  const [showDischargeUploadModal, setShowDischargeUploadModal] = useState(false);
  const [showDirectriceModal, setShowDirectriceModal] = useState(false);
  const [showCommercialModal, setShowCommercialModal] = useState(false);
  const [showDistributeurModal, setShowDistributeurModal] = useState(false);
  const [uploadingAttribution, setUploadingAttribution] = useState(false);
  const [pendingValidation, setPendingValidation] = useState(null);
  const [pendingDocument, setPendingDocument] = useState(null);
  const [fdvSignatureStatus, setFdvSignatureStatus] = useState(null);
  const [sourceCategory, setSourceCategory] = useState(null);
  // Vrai si la source d'approvisionnement désigne un distributeur précis (pas seulement une zone)
  const [sourceHasDistributor, setSourceHasDistributor] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionRows, setRevisionRows] = useState([]);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  // Une seule tentative de prise en charge automatique par ouverture de page.
  const autoAssignTried = useRef(false);

  useEffect(() => {
    const FDV_STATUS_ROLES = ['animateur', 'chef_de_zone', 'force_de_vente', 'directrice_marketing'];
    if (request?.currentStage === 'force_de_vente' && FDV_STATUS_ROLES.includes(user?.role)) {
      api.forceDeVente.getSignatureStatus(request._id)
        .then(res => {
          const status = res.data?.data;
          setFdvSignatureStatus(status);

          // Le créateur (animateur / chef de zone) prend en charge automatiquement sa propre
          // demande dès qu'elle arrive à l'étape FDV : décharge + QR générés, il atterrit
          // directement sur les signatures. Les autres demandes restent en prise en charge manuelle.
          const isCreator = String(request.createdBy || '') === String(user._id || user.id || '');
          // Uniquement si la source désigne déjà un distributeur ; sinon l'animateur le choisit dans la modale.
          if (!status && isCreator && sourceHasDistributor && ['animateur', 'chef_de_zone'].includes(user.role) && !autoAssignTried.current) {
            autoAssignTried.current = true;
            api.forceDeVente.assign(request._id)
              .then(() => api.forceDeVente.getSignatureStatus(request._id))
              .then(r => { setFdvSignatureStatus(r.data?.data); setShowForceDeVenteModal(true); })
              .catch(err => toast.error(err.response?.data?.error || 'Prise en charge automatique impossible — utilisez le bouton « Prendre en charge »'));
          }
        })
        .catch(() => {});
    }
  }, [request?.currentStage, request?._id, request?.createdBy, user?.role, user?._id, user?.id, sourceHasDistributor]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch request details with all related data
      const response = await api.get(`/api/requests/${id}`);
      const responseData = response.data.data;
      
      // Extract data from the response
      setRequest({ ...responseData.request, motifLabel: responseData.motifLabel });
      setProducts(responseData.products);
      setValidations(responseData.validations);
      setDocuments(responseData.documents);

      // Catégorie de la source d'approvisionnement (pour la frise d'étapes) — non bloquant
      try {
        const ssRes = await api.marketingValidation.getRequestSupplySource(id);
        setSourceCategory(ssRes.data?.data?.category || null);
        setSourceHasDistributor(!!ssRes.data?.data?.distributor?.id);
      } catch {
        setSourceCategory(null);
        setSourceHasDistributor(false);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error('Erreur lors du chargement des détails de la demande');
      setLoading(false);
      navigate('/requests');
    }
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const handleValidate = async () => {
    const effectiveType = request.workflowType || request.requestType;

    // Si l'utilisateur est du département marketing, afficher la modal de validation marketing
    if (request.currentStage === 'marketing' && (user.role === 'marketing' || (effectiveType !== 'business_unit' && user.role === 'directrice_marketing'))) {
      setShowMarketingValidationModal(true);
    }
    // Approbation d'un signataire sur une validation posée par un non-signataire
    else if (request.currentStage === 'marketing_approbation') {
      setShowApprobationModal(true);
    }
    // Revue marketing post-FDV
    else if (request.currentStage === 'marketing_review' && user.role === 'marketing') {
      setShowMarketingReviewModal(true);
    }
    // Si l'utilisateur est de la force de vente
    else if (request.currentStage === 'force_de_vente' && (user.role === 'animateur' || user.role === 'chef_de_zone')) {
      // Vérifier si une validation existe déjà avec le statut "pending_scan"
      try {
        const pendingValidation = validations.find(v => 
          v.stage === 'force_de_vente' && 
          v.status === 'pending_scan'
        );
        
        if (pendingValidation) {
          // Si une validation en attente de scan existe, chercher le document associé
          const dechargeDoc = documents.find(doc => 
            doc.type === 'decharge' && 
            doc.validationId && 
            doc.validationId.toString() === pendingValidation._id.toString()
          );
          
          if (dechargeDoc) {
            // Afficher directement le modal de téléversement du scan
            setPendingValidation(pendingValidation);
            setPendingDocument(dechargeDoc);
            setShowDischargeUploadModal(true);
          } else {
            // Si le document n'est pas trouvé, afficher le modal de validation normal
            setShowForceDeVenteModal(true);
          }
        } else {
          // Si aucune validation en attente n'existe, afficher le modal de validation normal
          setShowForceDeVenteModal(true);
        }
      } catch (error) {
        console.error('Error checking pending validation:', error);
        // En cas d'erreur, afficher le modal de validation normal
        setShowForceDeVenteModal(true);
      }
    }
    // Le distributeur signe la décharge depuis son compte
    else if (request.currentStage === 'distributeur' && user.role === 'distributeur') {
      setShowDistributeurModal(true);
    }
    // Visa du chef de zone : pas de document, validation directe
    else if (request.currentStage === 'chef_de_zone' && user.role === 'chef_de_zone') {
      handleChefDeZoneValidation();
    }
    // Si l'utilisateur est la directrice marketing, afficher la modal de validation directrice
    else if (request.currentStage === 'directrice' && actsAsDirectrice) {
      setShowDirectriceModal(true);
    }
    // Si l'utilisateur est du service commercial, afficher la modal de validation commerciale
    else if (request.currentStage === 'commercial' && (user.role === 'commercial' || user.department === 'commercial')) {
      setShowCommercialModal(true);
    }
    // Pour les autres départements, utiliser la validation standard
    else {
      handleStandardValidation();
    }
  };
  
  // Fonction appelée après la validation par la force de vente (étape 1)
  const handleForceDeVenteValidated = (data) => {
    // Stocker la validation et le document pour l'étape 2
    setPendingValidation(data.validation);
    setPendingDocument(data.document);
    
    // Afficher la modal d'upload du scan
    setShowDischargeUploadModal(true);
  };
  
  // Fonction appelée après l'upload du scan de la décharge (étape 2)
  const handleDischargeUploaded = (data) => {
    // Rafraîchir les détails de la demande
    fetchRequestDetails();
    
    // Réinitialiser les états
    setPendingValidation(null);
    setPendingDocument(null);
  };
  
  const handleChefDeZoneValidation = async () => {
    try {
      setValidationLoading(true);
      await api.chefDeZone.validate(id, {});
      toast.success('Demande validée');
      fetchRequestDetails();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la validation de la demande');
    } finally {
      setValidationLoading(false);
    }
  };

  // L'attribution est signée à la main par le bénéficiaire : l'animateur en
  // téléverse le scan, qui conditionne la validation de l'étape force de vente.
  const handleSignedAttributionUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingAttribution(true);
      const res = await api.forceDeVente.uploadSignedAttribution(id, formData);
      toast.success(res.data?.data?.message || 'Attribution signée téléversée');
      fetchRequestDetails();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors du téléversement');
    } finally {
      setUploadingAttribution(false);
    }
  };

  const handleStandardValidation = async () => {
    try {
      setValidationLoading(true);
      
      await api.put(`/api/requests/${id}/validate`);
      
      toast.success('Demande validée avec succès');
      fetchRequestDetails();
      setValidationLoading(false);
    } catch (error) {
      console.error('Error validating request:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la validation de la demande');
      setValidationLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Veuillez fournir un motif de refus');
      return;
    }
    
    try {
      setValidationLoading(true);

      // Les deux étapes ajoutées ont leurs propres routes de refus.
      if (user.role === 'distributeur') {
        await api.distributeur.reject(id, { rejectionReason: rejectReason });
      } else if (request.currentStage === 'chef_de_zone') {
        await api.chefDeZone.reject(id, { rejectionReason: rejectReason });
      } else {
        await api.put(`/api/requests/${id}/reject`, {
          rejectionReason: rejectReason,
        });
      }
      
      toast.success('Demande refusée');
      setShowRejectModal(false);
      fetchRequestDetails();
      setValidationLoading(false);
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.error || 'Erreur lors du refus de la demande');
      setValidationLoading(false);
    }
  };

  const downloadDocument = async (documentId, documentName) => {
    try {
      const response = await api.get(`/api/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement du document');
    }
  };

  const handleCancelDischarge = async () => {
    if (!window.confirm('Confirmer l\'annulation de cette décharge ?')) return;
    try {
      await api.put(`/api/force-de-vente/cancel-discharge/${id}`);
      toast.success('Décharge annulée');
      fetchRequestDetails();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de l\'annulation');
    }
  };

  const updateReimbursementStatus = async (status) => {
    try {
      await api.put(`/api/commercial/reimbursement/${id}`, { status });
      toast.success('Statut de remboursement mis à jour');
      fetchRequestDetails();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const getDepartmentText = (department) => {
    switch (department) {
      case 'marketing':
        return 'Marketing';
      case 'force_de_vente':
        return 'Force de Vente';
      case 'commercial':
        return 'Commercial';
      default:
        return department;
    }
  };
  
  const getDocumentDisplayName = (filename) => {
    if (filename.startsWith('attribution_')) {
      return 'Attribution de produit';
    } else if (filename.startsWith('decharge_')) {
      return 'Décharge';
    } else if (filename.startsWith('noteInterne_')) {
      return 'Note interne';
    } else if (filename.startsWith('remboursement_')) {
      if (filename.includes('_uht')) {
        return "Note d'avoir UHT";
      } else if (filename.includes('_frais')) {
        return "Note d'avoir produit frais";
      }
      return "Note d'avoir";
    }
    return filename;
  };

  // Révision post-validation : réservée aux demandes Business Unit déjà validées
  // par la directrice, accessible au créateur business_unit et à la directrice.
  const canRequestRevision = () => {
    if (!user || !request) return false;
    const effectiveType = request.workflowType || request.requestType;
    return (
      effectiveType === 'business_unit' &&
      ['directrice_validated', 'completed'].includes(request.status) &&
      !request.pendingRevision &&
      ['business_unit', 'directrice_marketing'].includes(user.role)
    );
  };

  const openRevisionModal = async () => {
    try {
      // Charger le catalogue produits et les produits actuels de la demande
      const [catalogRes, currentRes] = await Promise.all([
        api.get('/api/products'),
        api.get(`/api/requests/${id}/products`),
      ]);
      setAllProducts(catalogRes.data.data || []);
      const current = (currentRes.data.data || []).map((p) => ({
        productId: p.productId || '',
        quantity: p.quantity || 1,
      }));
      setRevisionRows(current.length > 0 ? current : [{ productId: '', quantity: 1 }]);
      setShowRevisionModal(true);
    } catch (error) {
      console.error('Error loading revision data:', error);
      toast.error('Erreur lors du chargement des produits');
    }
  };

  const updateRevisionRow = (index, field, value) => {
    setRevisionRows((rows) => {
      const next = [...rows];
      next[index] = {
        ...next[index],
        [field]: field === 'quantity' ? Math.max(1, parseInt(value) || 0) : value,
      };
      return next;
    });
  };

  const addRevisionRow = () => {
    setRevisionRows((rows) => [...rows, { productId: '', quantity: 1 }]);
  };

  const removeRevisionRow = (index) => {
    setRevisionRows((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== index) : rows));
  };

  const submitRevision = async () => {
    const cleaned = revisionRows.filter((r) => r.productId);
    if (cleaned.length === 0) {
      toast.error('Au moins un produit doit être sélectionné');
      return;
    }
    try {
      setRevisionLoading(true);
      await api.post(`/api/requests/${id}/revision`, {
        selectedProducts: JSON.stringify(cleaned.map((r) => ({ productId: r.productId, quantity: r.quantity }))),
      });
      toast.success('Révision soumise à la directrice');
      setShowRevisionModal(false);
      fetchRequestDetails();
    } catch (error) {
      console.error('Error submitting revision:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la demande de révision');
    } finally {
      setRevisionLoading(false);
    }
  };

  const canValidate = () => {
    if (!user || !request) return false;

    // Étape distributeur (après le chef de zone) : il signe tant qu'il n'a pas signé.
    if (user.role === 'distributeur') {
      return !request.status.includes('rejected')
        && request.currentStage === 'distributeur'
        && !request.distributeurValidatedAt;
    }
    
    // Check if the request is already completed or rejected
    if (request.status === 'completed' || request.status.includes('rejected')) {
      return false;
    }
    
    // Bloquer si l'étape courante a déjà été validée (statut final uniquement, pas 'processing')
    const alreadyValidatedCurrentStage = validations.find(validation => {
      if (validation.status === 'processing') return false; // FDV en cours, pas encore terminé

      const stage = request.currentStage;
      if (stage === 'marketing' && (user.role === 'marketing' || user.department === 'marketing') && validation.stage === 'marketing') return true;
      if (stage === 'marketing_review' && user.role === 'marketing' && validation.stage === 'marketing_review') return true;
      if (stage === 'force_de_vente' && (user.role === 'animateur' || user.role === 'chef_de_zone') && validation.stage === 'force_de_vente' && validation.status === 'validated') return true;
      if (stage === 'distributeur' && user.role === 'distributeur' && validation.stage === 'distributeur') return true;
      if (stage === 'chef_de_zone' && user.role === 'chef_de_zone' && validation.stage === 'chef_de_zone') return true;
      if (stage === 'directrice' && actsAsDirectrice && validation.stage === 'directrice') return true;
      if (stage === 'commercial' && (user.role === 'commercial' || user.department === 'commercial') && validation.stage === 'commercial') return true;
      return false;
    });

    if (alreadyValidatedCurrentStage) {
      return false;
    }
    
    // Cas spécial pour les demandes Business Unit
    if (request.requestType === 'business_unit') {
      // Seule la directrice marketing (ou son intérimaire) valide les demandes Business Unit
      return actsAsDirectrice;
    }
    
    // Vérifier si l'utilisateur a le bon rôle/département pour l'étape actuelle
    // Priorité à l'étape actuelle (currentStage) plutôt qu'au statut
    
    // Si l'étape actuelle est marketing, tout utilisateur du marketing peut valider
    if (request.currentStage === 'marketing' && (user.role === 'directrice_marketing'
      || user.role === 'marketing' || user.department === 'marketing')) {
      return true;
    }

    // Approbation signataire : seuls les signataires (et la directrice) débloquent.
    if (request.currentStage === 'marketing_approbation' && (user.role === 'directrice_marketing'
      || ((user.role === 'marketing' || user.department === 'marketing') && user.isSignatory))) {
      return true;
    }
    
    // Si l'étape actuelle est force_de_vente, seuls les animateurs (de la zone) prennent en charge la demande
    if (request.currentStage === 'force_de_vente' && (user.role === 'animateur' || user.role === 'chef_de_zone')) {
      return true;
    }
    
    // Si l'étape actuelle est marketing_review, le marketing peut approuver/rejeter
    if (request.currentStage === 'marketing_review' && user.role === 'marketing') {
      return true;
    }

    // Étape distributeur : seul le compte du distributeur rattaché signe
    if (request.currentStage === 'distributeur' && user.role === 'distributeur') {
      return true;
    }

    // Étape chef de zone : visa du chef de la zone de la demande
    if (request.currentStage === 'chef_de_zone' && user.role === 'chef_de_zone') {
      return true;
    }

    // Si l'étape actuelle est directrice, seule la directrice marketing peut valider
    if (request.currentStage === 'directrice' && actsAsDirectrice) {
      return true;
    }
    
    // Si l'étape actuelle est commercial, tout utilisateur du commercial peut valider
    if (request.currentStage === 'commercial' && (user.role === 'commercial' || user.department === 'commercial')) {
      return true;
    }
    
    // Fallback sur l'ancien comportement basé sur le statut (pour compatibilité)
    if (request.status === 'pending' && (user.role === 'marketing' || user.department === 'marketing')) {
      return true;
    }
    
    if (request.status === 'marketing_validated' && user.role === 'animateur') {
      return true;
    }
    
    if (request.status === 'force_de_vente_validated' && actsAsDirectrice && request.currentStage === 'directrice') {
      return true;
    }
    
    if (request.status === 'directrice_validated' && (user.role === 'commercial' || user.department === 'commercial')) {
      return true;
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Demande non trouvée
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Demande terrain avec attribution : le scan signé fait avancer la demande, sans bouton Valider.
  const attenteScanTerrain = !!request.circuitTerrain
    && request.currentStage === 'force_de_vente'
    && fdvSignatureStatus?.status === 'processing'
    && fdvSignatureStatus.requiresBeneficiaire !== false
    && !request.signedAttributionUrl;
  const suiteTerrain = request.circuitTerrain === 'chef_de_zone' ? 'au distributeur' : 'au chef de zone';

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Détails de la demande {request?.referenceId || `#${id.substring(0, 8)}`}
        </h1>
        <button
          onClick={() => navigate('/requests')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour
        </button>
      </div>
      
      <WorkflowStepper
        workflowType={request.workflowType || request.requestType}
        sourceCategory={sourceCategory}
        currentStage={request.currentStage}
        status={request.status}
        requestType={request.requestType}
        circuitTerrain={request.circuitTerrain}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Informations de la demande</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Statut</p>
                <p className="mt-1 flex flex-wrap gap-2 items-center">
                  {(() => {
                    const badge = getStatusBadge(request.status, request.currentStage, fdvSignatureStatus?.status);
                    return (
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.classes}`}>
                        {badge.label}
                      </span>
                    );
                  })()}
                  {request.pendingRevision && (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                      Révision en attente de la directrice
                    </span>
                  )}
                  {request.reimbursementStatus && request.reimbursementStatus !== 'none' && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      request.reimbursementStatus === 'en_cours'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {request.reimbursementStatus === 'en_cours' ? 'Remboursement en cours' : 'Remboursement effectué'}
                    </span>
                  )}
                </p>
                {['commercial', 'directrice_marketing'].includes(user?.role) && request.status === 'completed' && (
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => updateReimbursementStatus('en_cours')}
                      className="bg-yellow-100 text-yellow-700 border border-yellow-300 px-3 py-1 rounded text-xs hover:bg-yellow-200"
                    >
                      Marquer en cours
                    </button>
                    <button
                      onClick={() => updateReimbursementStatus('effectue')}
                      className="bg-green-100 text-green-700 border border-green-300 px-3 py-1 rounded text-xs hover:bg-green-200"
                    >
                      Marquer effectué
                    </button>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Date de création</p>
                <p className="mt-1 font-medium">{new Date(request.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Dernière mise à jour</p>
                <p className="mt-1 font-medium">{new Date(request.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Informations du demandeur</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nom</p>
                <p className="mt-1 font-medium">{request.externalUserName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="mt-1 font-medium">{request.externalUserEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="mt-1 font-medium">{request.externalUserPhone || 'Non spécifié'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Organisation</p>
                <p className="mt-1 font-medium">{request.organization || 'Non spécifiée'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Raison de la demande</h2>
            <p className="whitespace-pre-line">{request.motifLabel || request.reason}</p>
            
            {request.additionalInfo && (
              <div className="mt-4">
                <h3 className="text-md font-medium mb-2">Informations complémentaires</h3>
                <p className="whitespace-pre-line">{request.additionalInfo}</p>
              </div>
            )}
          </div>

          {/* Pièces jointes — visibles par les comptes marketing */}
          {(user?.role === 'marketing' || user?.department === 'marketing' || user?.role === 'directrice_marketing') && (() => {
            const attachmentList = (request.attachments && request.attachments.length > 0)
              ? request.attachments
              : (request.attachmentUrl
                  ? [{ url: request.attachmentUrl, originalName: request.attachmentOriginalName }]
                  : []);
            const isImage = (url) => /\.(png|jpe?g|gif|webp)$/i.test(url || '');
            return (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4">
                  Pièces jointes
                  {attachmentList.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">({attachmentList.length})</span>
                  )}
                </h2>
                {attachmentList.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune pièce jointe fournie.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {attachmentList.map((att, index) => {
                      const href = `/${att.url}`;
                      const name = att.originalName || `Pièce jointe ${index + 1}`;
                      return (
                        <a
                          key={index}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex flex-col border border-gray-200 rounded-lg overflow-hidden hover:border-primary-400 hover:shadow-md transition-all duration-200"
                        >
                          {isImage(att.url) ? (
                            <img src={href} alt={name} className="h-32 w-full object-cover bg-gray-50" />
                          ) : (
                            <div className="h-32 w-full flex items-center justify-center bg-gray-50">
                              <svg className="h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="p-3 flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-700 truncate group-hover:text-primary-600">{name}</span>
                            <svg className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Produits demandés</h2>
              {['commercial', 'supply_chain', 'directrice_marketing'].includes(user?.role) && (
                <button
                  onClick={async () => {
                    try {
                      const res = await api.requests.exportProducts(id);
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      const today = new Date().toISOString().slice(0, 10);
                      a.download = `produits-${request?.referenceId || id}-${today}.xlsx`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch {
                      toast.error("Erreur lors de l'export Excel");
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-green-600 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Exporter les produits (Excel)
                </button>
              )}
            </div>
            {products.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code SAP</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {product.productId && typeof product.productId === 'object'
                            ? product.productId.name
                            : (product.productName || 'Produit inconnu')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {product.productId && typeof product.productId === 'object'
                            ? (product.productId.sapCode || '—')
                            : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.productId && typeof product.productId === 'object'
                            ? product.productId.category
                            : (product.productCategory || 'Catégorie inconnue')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">Aucun produit demandé</p>
            )}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Validation Actions */}
          {canValidate() && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Actions</h2>
              <div className="space-y-3">
                {!attenteScanTerrain && (
                <button
                  onClick={handleValidate}
                  disabled={validationLoading}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {validationLoading
                    ? 'Validation...'
                    : (request?.currentStage === 'force_de_vente' && user.role !== 'distributeur')
                      ? (fdvSignatureStatus?.status === 'processing'
                          ? "Valider l'étape FDV"
                          : ((user.role === 'animateur' || user.role === 'chef_de_zone') ? 'Prendre en charge la demande' : 'Assigner la demande'))
                      : user.role === 'distributeur'
                      ? 'Signer la décharge'
                      : request?.currentStage === 'marketing_approbation'
                      ? 'Approuver et signer le document'
                      : 'Valider la demande'}
                </button>
                )}
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={validationLoading}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {validationLoading ? 'Refus...' : 'Refuser la demande'}
                </button>
              </div>
              {request?.currentStage === 'force_de_vente'
                && fdvSignatureStatus?.status === 'processing'
                && fdvSignatureStatus.requiresBeneficiaire !== false
                && !request.signedAttributionUrl && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">Attribution signée manquante</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Imprimez le document d'attribution, faites-le signer par le bénéficiaire,
                    puis téléversez le scan ci-dessous
                    {attenteScanTerrain ? ` : la demande sera transmise ${suiteTerrain}.` : ' avant de valider.'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Attribution signée à la main : impression, signature papier, puis scan */}
          {(user?.role === 'animateur' || user?.role === 'chef_de_zone') &&
           request?.currentStage === 'force_de_vente' &&
           fdvSignatureStatus?.status === 'processing' &&
           fdvSignatureStatus.requiresBeneficiaire !== false && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-1">Attribution signée</h2>
              <p className="text-xs text-gray-500 mb-4">
                Téléchargez le document d'attribution ci-dessous, faites-le signer par le bénéficiaire,
                puis téléversez-en le scan (PDF, JPG ou PNG).
                {request.circuitTerrain && ` Le téléversement transmet la demande ${suiteTerrain}.`}
              </p>
              {request.signedAttributionUrl ? (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                  <p className="text-sm text-green-700 font-medium">✓ Scan reçu</p>
                  <a
                    href={`/${request.signedAttributionUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                  >
                    Voir / Télécharger
                  </a>
                </div>
              ) : (
                <label className="block">
                  <span className="sr-only">Scan de l'attribution signée</span>
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    disabled={uploadingAttribution}
                    onChange={(e) => handleSignedAttributionUpload(e.target.files?.[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                  />
                </label>
              )}
              {uploadingAttribution && (
                <p className="mt-2 text-xs text-gray-500">Téléversement en cours...</p>
              )}
            </div>
          )}

          {/* Section spéciale pour téléverser la décharge signée */}
          {!canValidate() &&
           request.status === 'marketing_validated' &&
           (user.role === 'force_de_vente' || user.role === 'chef_de_zone' || user.department === 'force_de_vente') &&
           validations.some(v => v.stage === 'force_de_vente' && v.status === 'pending_scan') && (
            <div className="bg-white rounded-lg shadow p-6 mb-6 border-2 border-blue-300">
              <h2 className="text-lg font-semibold mb-4 text-blue-700">Finaliser la validation</h2>
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-3">
                  Vous avez déjà généré une décharge pour cette demande. Veuillez la téléverser une fois signée pour finaliser la validation.
                </p>
                <button
                  onClick={() => {
                    // Trouver la validation et le document associé
                    const pendingValidation = validations.find(v => 
                      v.stage === 'force_de_vente' && 
                      v.status === 'pending_scan'
                    );
                    
                    if (pendingValidation) {
                      const dechargeDoc = documents.find(doc => 
                        doc.type === 'decharge' && 
                        doc.validationId && 
                        doc.validationId.toString() === pendingValidation._id.toString()
                      );
                      
                      if (dechargeDoc) {
                        setPendingValidation(pendingValidation);
                        setPendingDocument(dechargeDoc);
                        setShowDischargeUploadModal(true);
                      }
                    }
                  }}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Téléverser la décharge signée
                </button>
              </div>
            </div>
          )}
          
          {/* Annulation décharge */}
          {['force_de_vente', 'chef_de_zone', 'directrice_marketing'].includes(user?.role) &&
           validations.some(v => v.stage === 'force_de_vente' && ['pending_scan', 'validated'].includes(v.status)) &&
           !validations.find(v => v.stage === 'directrice' && v.status === 'validated') && (
            <div className="bg-white rounded-lg shadow p-6 mb-6 border border-red-200">
              <h2 className="text-lg font-semibold mb-2 text-red-700">Annuler la décharge</h2>
              <p className="text-sm text-gray-600 mb-3">
                Permet d'annuler la décharge en cours et de remettre la demande en attente de validation FDV.
              </p>
              <button
                onClick={handleCancelDischarge}
                className="bg-red-100 text-red-700 border border-red-300 px-4 py-2 rounded text-sm hover:bg-red-200"
              >
                Annuler la décharge
              </button>
            </div>
          )}

          {/* Source d'approvisionnement */}
          {request.status !== 'pending' && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Source d'approvisionnement</h2>
              <SupplySourceHistory requestId={id} />
            </div>
          )}
          
          {/* Documents */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Documents</h2>
              {canRequestRevision() && (
                <button
                  type="button"
                  onClick={openRevisionModal}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Demander une modification
                </button>
              )}
            </div>
            {(() => {
              const fdvVal = validations?.find(v => v.stage === 'force_de_vente' && v.dischargeScannedUrl);
              return fdvVal ? (
                <div className="flex items-center justify-between p-3 mb-2 bg-green-50 rounded border border-green-200">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Décharge signée (scan distributeur)</p>
                    <p className="text-xs text-gray-500">{fdvVal.validationDate ? new Date(fdvVal.validationDate).toLocaleDateString() : ''}</p>
                  </div>
                  <a
                    href={`/${fdvVal.dischargeScannedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                  >
                    Voir / Télécharger
                  </a>
                </div>
              ) : null;
            })()}
            {(() => {
              const dischargeDoc = documents.find(doc => doc.type === 'decharge');
              if (!dischargeDoc) return null;
              return (
                <div className="mt-3 p-3 rounded border border-blue-200 bg-blue-50">
                  <p className="text-sm font-medium text-gray-900 mb-2">Signature du distributeur</p>
                  {dischargeDoc.recipientSigned ? (
                    <div>
                      <p className="text-xs text-green-700 font-medium">✓ Signé le {formatDate(dischargeDoc.recipientSignedAt)}</p>
                      <p className="text-xs text-gray-600">{dischargeDoc.recipientSignerName ?? '—'} — {dischargeDoc.recipientSignerFunction ?? '—'}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-orange-600 font-medium">
                      En attente de la signature du distributeur depuis son compte
                    </p>
                  )}
                </div>
              );
            })()}
            {request.signedAttributionUrl && !documents.some((d) => d.type === 'attribution' && d.beneficiarySigned) && (
              <div className="flex items-center justify-between p-3 mt-3 bg-green-50 rounded border border-green-200">
                <p className="text-sm font-medium text-gray-900">Attribution signée (scan bénéficiaire)</p>
                <a
                  href={`/${request.signedAttributionUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                >
                  Voir / Télécharger
                </a>
              </div>
            )}
            {documents.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {documents.map((doc) => (
                  <li key={doc._id} className={`py-3 ${doc.obsolete ? 'opacity-60' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium text-gray-900 ${doc.obsolete ? 'line-through' : ''}`}>
                          {getDocumentDisplayName(doc.filename)}
                          {doc.type === 'attribution' && doc.beneficiarySigned && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                              Signée par le bénéficiaire
                            </span>
                          )}
                          {doc.type === 'attribution' && !doc.beneficiarySigned && !doc.signatureMetadata?.signature && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                              Non signé — en attente du signataire
                            </span>
                          )}
                          {doc.obsolete && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 no-underline">
                              ✕ Document obsolète
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <button
                          onClick={() => downloadDocument(doc._id, doc.filename)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Télécharger
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              !validations?.find(v => v.stage === 'force_de_vente' && v.dischargeScannedUrl) && (
                <p className="text-gray-500">Aucun document disponible</p>
              )
            )}
          </div>
          
          {/* Validation History */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Historique des validations</h2>
            {validations.length > 0 ? (
              <div className="flow-root">
                <ul className="-mb-8">
                  {validations.map((validation, index) => (
                    <li key={validation._id}>
                      <div className="relative pb-8">
                        {index !== validations.length - 1 ? (
                          <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                              validation.status === 'validated' ? 'bg-green-500' : 
                              validation.status === 'rejected' ? 'bg-red-500' : 
                              validation.status === 'pending_scan' ? 'bg-blue-500' : 'bg-gray-500'
                            }`}>
                              {validation.status === 'validated' ? (
                                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : validation.status === 'rejected' ? (
                                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              ) : validation.status === 'pending_scan' ? (
                                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                </svg>
                              )}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">
                                {validation.status === 'validated' ? 'Validé par ' : 
                                 validation.status === 'rejected' ? 'Refusé par ' : 
                                 validation.status === 'pending_scan' ? 'Validé, en attente de signature par ' : 
                                 'Statut inconnu par '}
                                <span className="font-medium text-gray-900">{validation.validatorId?.name || 'Utilisateur inconnu'}</span>
                                {' '}
                                ({validation.stage ? getDepartmentText(validation.stage) : 'Étape inconnue'})
                              </p>
                              {validation.comment && (
                                <p className="mt-1 text-sm text-gray-500">Motif: {validation.comment}</p>
                              )}
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              {new Date(validation.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-gray-500">Aucune validation effectuée</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Marketing Validation Modal */}
      {showMarketingValidationModal && (
        <MarketingValidationModal
          requestId={id}
          onClose={() => setShowMarketingValidationModal(false)}
          onSuccess={() => {
            setShowMarketingValidationModal(false);
            fetchRequestDetails();
          }}
        />
      )}
      
      {/* Force de Vente Validation Modal */}
      {showForceDeVenteModal && (
        <ForceDeVenteValidationModal
          requestId={id}
          onClose={() => setShowForceDeVenteModal(false)}
          onSuccess={() => { setShowForceDeVenteModal(false); fetchRequestDetails(); }}
          distributorPrechosen={sourceHasDistributor}
          circuitTerrain={request.circuitTerrain}
        />
      )}
      
      {/* Discharge Upload Modal */}
      {showDischargeUploadModal && pendingDocument && (
        <DischargeUploadModal
          requestId={id}
          documentUrl={pendingDocument.path}
          documentId={pendingDocument.id || pendingDocument._id}
          onClose={() => {
            setShowDischargeUploadModal(false);
            setPendingValidation(null);
            setPendingDocument(null);
          }}
          onUploaded={handleDischargeUploaded}
        />
      )}
      
      {/* Directrice Validation Modal */}
      {showDirectriceModal && (
        <DirectriceValidationModal
          requestId={id}
          requestType={request.requestType}
          onClose={() => setShowDirectriceModal(false)}
          onValidated={() => {
            setShowDirectriceModal(false);
            fetchRequestDetails();
          }}
        />
      )}
      
      {/* Commercial Validation Modal */}
      {showCommercialModal && (
        <CommercialValidationModal
          requestId={id}
          onClose={() => setShowCommercialModal(false)}
          onValidated={() => {
            setShowCommercialModal(false);
            fetchRequestDetails();
          }}
        />
      )}

      {/* Distributeur Validation Modal */}
      {showDistributeurModal && (
        <DistributeurValidationModal
          requestId={id}
          mode="validate"
          onClose={() => setShowDistributeurModal(false)}
          onSuccess={fetchRequestDetails}
        />
      )}

      {/* Marketing Review Modal (post-FDV) */}
      {showMarketingReviewModal && (
        <MarketingReviewModal
          requestId={id}
          onClose={() => setShowMarketingReviewModal(false)}
          onValidated={() => {
            setShowMarketingReviewModal(false);
            fetchRequestDetails();
          }}
        />
      )}

      {showApprobationModal && (
        <MarketingReviewModal
          requestId={id}
          title="Approbation signataire"
          submit={api.marketingValidation.approbation}
          approveNote="Le document d'attribution sera signé avec votre signature et votre cachet, puis la demande repartira dans le workflow."
          onClose={() => setShowApprobationModal(false)}
          onValidated={() => {
            setShowApprobationModal(false);
            fetchRequestDetails();
          }}
        />
      )}
      
      {/* Revision Modal (Business Unit) */}
      {showRevisionModal && (
        <Modal
          open
          onClose={() => setShowRevisionModal(false)}
          title="Modifier les produits"
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowRevisionModal(false)}
                disabled={revisionLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={submitRevision}
                disabled={revisionLoading}
                className="modal-btn-primary"
              >
                {revisionLoading ? 'Envoi en cours...' : 'Soumettre la révision'}
              </button>
            </>
          }
        >
          <div>
                <p className="text-sm text-gray-500 mb-4">
                  La demande sera renvoyée à la directrice. L'ancienne note interne sera marquée obsolète et une nouvelle note sera générée.
                </p>

                {revisionRows.map((row, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <div className="flex-grow">
                      <label htmlFor={`revision-product-${index}`} className="sr-only">Produit</label>
                      <select
                        id={`revision-product-${index}`}
                        value={row.productId}
                        onChange={(e) => updateRevisionRow(index, 'productId', e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm cursor-pointer"
                      >
                        <option value="">Sélectionnez un produit</option>
                        {Array.isArray(allProducts) && allProducts.map((p) => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="w-28">
                      <label htmlFor={`revision-qty-${index}`} className="sr-only">Quantité</label>
                      <input
                        id={`revision-qty-${index}`}
                        type="number"
                        min="1"
                        value={row.quantity}
                        onChange={(e) => updateRevisionRow(index, 'quantity', e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRevisionRow(index)}
                      disabled={revisionRows.length === 1}
                      aria-label="Retirer le produit"
                      className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addRevisionRow}
                  className="mt-2 flex items-center text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Ajouter un produit
                </button>
          </div>
        </Modal>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <Modal
          open
          onClose={() => setShowRejectModal(false)}
          title="Refuser la demande"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowRejectModal(false)}
                disabled={validationLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="modal-btn-danger"
                onClick={handleReject}
                disabled={validationLoading}
              >
                {validationLoading ? 'Refus...' : 'Refuser'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Veuillez fournir un motif de refus. Ce motif sera visible par le demandeur et les autres utilisateurs.
              </p>
              <div className="mt-4">
                <textarea
                  rows="4"
                  className="shadow-sm block w-full focus:ring-blue-500 focus:border-blue-500 sm:text-sm border border-gray-300 rounded-md"
                  placeholder="Motif du refus"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                ></textarea>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RequestDetails;
